import userActions from "actions/user";
import { Table, Button, Person, TableLoader, STATUS_TYPES, Notification, generateResolver, yup } from "dyl-components";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Grid, Header, Icon, Popup } from "semantic-ui-react";
import ResendEmailForm from "shared/forms/ResendEmailForm";
import authActions from "actions/auth/auth";
import basePath from "actions/basePath";
import { useController, useForm } from "react-hook-form";
import { Form } from "semantic-ui-react";

const SimulateUserForm = ({ user_id, onClose }) => {
    const { control, handleSubmit, formState: { isValid, isDirty } } = useForm({
        mode: 'onChange',
        resolver: generateResolver({
            password: yup.string().required("Password is required"),
        }),
        defaultValues: {
            password: "",
        },
    });
    const { isSimulatingUser } = useSelector((state) => state.auth);
    const { field } = useController({ control, name: 'password' });
    const dispatch = useDispatch();

    const onSubmit = async (data) => {
        try {
            const response = await dispatch(
                authActions.simulateUser({
                    new_user_id: user_id,
                    password: data.password,
                })
            );
            window.open(
                `${basePath.USER_HOST}/login?simulate_token=${response.token}`,
                "_blank",
                "noopener,noreferrer"
            );
            onClose();
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to simulate user", STATUS_TYPES.ERROR);
        }
    };
    return (
        <Form noValidate loading={isSimulatingUser}>
            <Form.Input label="Enter your password" required type="password" placeholder="Password" value={field.value} onChange={(_, { value }) => field.onChange({ target: { name: field.name, value } })} />
            <Button disabled={isSimulatingUser || !isValid || !isDirty} type="submit" onClick={handleSubmit(onSubmit)} primary>Confirm</Button>
        </Form>
    );
};

const Users = () => {
    const { summarized_users, isReadingSummarizedUsers, signed_terms } = useSelector(state => state.user);
    const isCheckingIfAccountHasOpenSeats = useSelector(state => state.subscription.isCheckingIfAccountHasOpenSeats);
    const [userToResendEmail, setUserToResendEmail] = useState(null);
    const [resendCooldowns, setResendCooldowns] = useState({});
    const { account_id } = useParams();
    const dispatch = useDispatch();
    const handleColldownTimeout = (userId) =>{

            setResendCooldowns(prev => ({
                ...prev,
                [userId]: Date.now() + 10000
            }));

            setTimeout(() => {
                setResendCooldowns(prev => {
                    const updated = { ...prev };
                    delete updated[userId];
                    return updated;
                });
            }, 10000);
    }
    const onResendEmail = async (email, userId) => {
        try {
            await dispatch(userActions.resendEmail({ email }));
            Notification.alert(`Email sent to ${email}`, STATUS_TYPES.SUCCESS);
            setUserToResendEmail(null);
            handleColldownTimeout(userId)
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to resend email", STATUS_TYPES.ERROR);
        }
    };
    const isResendingEmail = useSelector(state => state.user.isResendingEmail);
    const customer_status = useSelector(state => state.account.account?.customer?.customer_status)
    const [userToSimulate, setUserToSimulate] = useState(null);
    const canResendEmail = useSelector(state => {
        return (state.account.account?.customer || {}).customer_status === "active" || state.subscription.hasOpenSeats
    })
    return (
        <Table>
            <Table.Header primary>
                <Table.Row>
                    <Table.HeaderCell colspan={2}>
                        <Icon className="fas fa-user-lock" /> User Account
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {isReadingSummarizedUsers || isCheckingIfAccountHasOpenSeats ? (
                    <Table.Row>
                        <TableLoader isLoading colspan={2} />
                    </Table.Row>
                ) : (
                    summarized_users?.map((user) => {
                        const isCooldownActive = resendCooldowns[user.user_id] && resendCooldowns[user.user_id] > Date.now();
                        const isResendEmailDisabled = !canResendEmail || isCooldownActive || !user.is_owner || customer_status === "canceled" || isResendingEmail;
                        return (
                            <Table.Row key={user.user_id}>
                                <Table.Cell>
                                    <Person
                                        popup={false}
                                    username={`${user.first_name} ${
                                        user.last_name
                                    } ${user.is_owner ? `(Owner)` : ''}`}
                                        email={user.email}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    {!user.has_log_in ? (
                                        <Popup 
                                            on='click'
                                            open={userToResendEmail === user.user_id}
                                            onOpen={() => setUserToResendEmail(user.user_id)}
                                            onClose={() => setUserToResendEmail(null)}
                                        trigger={<Button floated='right' disabled={!user.is_owner || isCooldownActive || isResendingEmail || customer_status==="canceled"}>Resend Email</Button>} 
                                    content={<>
                                        <Header color='primary'>Resend Email</Header>
                                        <ResendEmailForm email={user.email} idOfUserToUpdate={user.user_id} customer_id={account_id} onClose={() => {setUserToResendEmail(null); handleColldownTimeout(user.user_id) }} />
                                    </>}
                                            position="bottom center"
                                        />
                                    ) : (
                                        <Button
                                            floated='right'
                                            disabled={isResendEmailDisabled}
                                            loading={isResendingEmail}
                                            onClick={() => onResendEmail(user.email, user.user_id)}
                                        >
                                            Resend Email
                                        </Button>
                                    )}
                                    <Popup
                                        disabled={!signed_terms}
                                        open={userToSimulate === user.user_id}
                                        trigger={
                                            <Button disabled={!signed_terms || !user?.has_log_in} floated="right">
                                                Simulate
                                            </Button>
                                        }
                                        onOpen={() => setUserToSimulate(user.user_id)}
                                        onClose={() => setUserToSimulate(null)}
                                        content={
                                        <SimulateUserForm user_id={user.user_id} onClose={() => setUserToSimulate(null)} />
                                        }
                                        on="click"
                                        position="bottom center"
                                    />
                                </Table.Cell>
                            </Table.Row>
                        );
                    })
                )}
            </Table.Body>
        </Table>
    );
};

const AdminSettings = ({ onGenerateAccount }) => {
    const { summarized_users, isReadingSummarizedUsers } = useSelector(state => state.user);
    const canAddUsers = useSelector(state => {
        return state.subscription.hasOpenSeats && !summarized_users?.length
    })
    const isCheckingIfAccountHasOpenSeats = useSelector(state => state.subscription.isCheckingIfAccountHasOpenSeats);
    return (
        <Grid>
            {!isReadingSummarizedUsers && !isCheckingIfAccountHasOpenSeats && canAddUsers && (
                <Grid.Row>
                    <Grid.Column>
                        <Button primary onClick={onGenerateAccount}>
                            Generate Account
                        </Button>
                    </Grid.Column>
                </Grid.Row>
            )}
            <Grid.Row>
                <Grid.Column width={10}>
                    <Users />
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

export default AdminSettings;
